import { Axis } from "../constants/axis.js";
import { Direction } from "../constants/direction.js";
import { PanEvent } from "../events/pan_event.js";
import { supportsTouch } from "../utilities/media.js";

/** @typedef {import("../types/components.js").DickTokComponent} DickTokComponent */
/** @typedef {import("../types/pan_gesture.js").PanGestureInterface} PanGestureInterface */
/** @typedef {import("../types/pan_gesture.js").PanInput} PanInput */
/** @typedef {import("../types/pan_gesture.js").PanMeasurement} PanMeasurement */
/** @typedef {import("../types/types.js").Coordinates} Coordinates */

const PanGesture = (superclass) => {
  return class extends superclass {
    /** @type {number} */
    #panThresholdPx = 10;
    /** @type {Set<Axis>} */
    #panDetectionAxis = new Set([Axis.HORIZONTAL, Axis.VERTICAL]);

    /** @type {Coordinates} */
    #origin;
    /** @type {number} */
    #height;
    /** @type {number} */
    #width;
    /** @type {ResizeObserver} */
    #resizeObserver;

    /** @type {boolean} */
    #isPanning = false;
    /** @type {?PanInput} */
    #panStart = null;
    /** @type {?PanInput} */
    #panEnd = null;

    /** @type {boolean} */
    #lockAxis = true;
    /** @type {?Axis} */
    #panAxis = null;
    /** @type {?Axis} */
    #initialAxis = null;

    connectedCallback() {
      super.connectedCallback();
      this.#measureElement();
      this.#updateCssProperties();
      supportsTouch() ? this.#addTouchListeners() : this.#addMouseListeners();
      this.enablePanning();
    }

    panEnabled(axis = null) {
      return axis
        ? axis === Axis.ALL
          ? this.#panDetectionAxis.size === 2
          : this.#panDetectionAxis.has(axis)
        : this.#panDetectionAxis.size > 0;
    }

    enablePanning(axis = Axis.ALL) {
      this.#addTrackedAxis(this.#panDetectionAxis, axis);
      this.#updateCssProperties();
    }

    disablePanning(axis = Axis.ALL) {
      this.#removeTrackedAxis(this.#panDetectionAxis, axis);
      this.#updateCssProperties();
    }

    #measureElement() {
      const { top, left } = this.getBoundingClientRect();
      this.#origin = { x: left, y: top };
      this.#width = this.offsetWidth;
      this.#height = this.offsetHeight;
      // This won't handle if the origin changes, but that's ok for now
      this.#resizeObserver = new ResizeObserver(([entry]) => {
        const {
          contentRect: { width, height },
        } = entry;
        // Very important not to update the DOM with these for performance
        // reasons, but we only use these for normalizing the coordinates
        this.#width = width;
        this.#height = height;
      });
      this.#resizeObserver.observe(this);
      this.disconnectSignal.addEventListener("abort", () => {
        this.#resizeObserver.disconnect();
      });
    }

    #addTouchListeners() {
      const options = {
        capture: true,
        passive: true,
        signal: this.disconnectSignal,
      };
      this.addEventListener(
        "touchstart",
        (event) => {
          if (event.touches.length === 1) {
            const touch = event.touches[0];
            this.#handlePanStart({ x: touch.clientX, y: touch.clientY });
          }
        },
        options,
      );
      this.addEventListener(
        "touchmove",
        (event) => {
          if (event.touches.length === 1) {
            const touch = event.touches[0];
            this.#handlePanMove({ x: touch.clientX, y: touch.clientY });
            if (this.#isPanning) {
              // If panning, prevent event from propagating to children
              event.stopPropagation();
            }
          }
        },
        options,
      );
      this.addEventListener(
        "touchend",
        (event) => {
          // Do this first as #handlePanEnd() will set isPanning = false
          if (this.#isPanning) {
            // If panning, prevent event from propagating to children
            event.stopPropagation();
          }
          this.#handlePanEnd();
        },
        options,
      );
      this.addEventListener(
        "touchcancel",
        (event) => {
          // Do this first as #handlePanCancel() will set isPanning = false
          if (this.#isPanning) {
            // If panning, prevent event from propagating to children
            event.stopPropagation();
          }
          this.#handlePanCancel();
        },
        options,
      );
    }

    #addMouseListeners() {
      const options = {
        capture: true,
        passive: true,
        signal: this.disconnectSignal,
      };
      this.addEventListener(
        "mousedown",
        (event) => {
          this.#handlePanStart({ x: event.clientX, y: event.clientY });
        },
        options,
      );
      this.addEventListener(
        "mousemove",
        (event) => {
          // Mouse emits this event all the time, so don't update unless
          // mousedown has fired and mouseup has not fired.
          if (!this.#panStart) {
            return;
          }
          this.#handlePanMove({ x: event.clientX, y: event.clientY });
          if (this.#isPanning) {
            // If panning, prevent events from propagating to children
            event.stopPropagation();
          }
        },
        options,
      );
      this.addEventListener(
        "mouseup",
        (event) => {
          if (!this.#panStart) {
            return;
          }
          // Do this first as #handlePanEnd() will set isPanning = false
          if (this.#isPanning) {
            // If panning, prevent events from propagating to children
            event.stopPropagation();
          }
          this.#handlePanEnd();
        },
        options,
      );
      this.addEventListener(
        "mouseout",
        (event) => {
          if (!this.#panStart) {
            return;
          }
          // Do this first as #handlePanCancel() will set isPanning = false
          if (this.#isPanning) {
            // If panning, prevent events from propagating to children
            event.stopPropagation();
          }
          this.#handlePanCancel();
        },
        options,
      );
    }

    /** @param {Coordinates} coordinates Client coordinates */
    #handlePanStart(coordinates) {
      if (this.#panDetectionAxis.size === 0) {
        return;
      }
      const { native, normalized } = this.#measurePanCoordinates(coordinates);
      this.#panStart = { native, normalized, time: Date.now() };
    }

    /** @param {Coordinates} coordinates Client coordinates */
    #handlePanMove(coordinates) {
      if (this.#panDetectionAxis.size === 0) {
        return;
      }
      const { native, normalized } = this.#measurePanCoordinates(coordinates);
      this.#panEnd = { native, normalized, time: Date.now() };

      const panMeasurement = this.#measurePan();
      this.#panAxis = panMeasurement.axis;

      // In panStart the user has just touched the screen, so check
      // if the user has exceeded the threshold to consider a pan
      if (!this.#isPanning) {
        const { distance } = panMeasurement;
        // Wait for the user to swipe a minimum distance before starting
        const isThresholdMet =
          distance.native.x > this.#panThresholdPx ||
          distance.native.y > this.#panThresholdPx;
        if (!isThresholdMet) {
          return;
        }
        // Check if we are tracking this axis
        if (!this.#panDetectionAxis.has(this.#panAxis)) {
          return;
        }
        // Update component state
        this.#isPanning = true;
        // Set the initial axis
        this.#initialAxis = this.#panAxis;
        // Dispatch event on this element for the start
        this.dispatchEvent(
          new CustomEvent(PanEvent.PAN_START, {
            // Default value, but make it clear this is not propagated
            bubbles: false,
            detail: {
              ...panMeasurement,
              start: this.#panStart,
              end: this.#panEnd,
            },
          }),
        );
      }

      // Don't send the pan move event unless the user has panned, but
      // this could have just changed above so check again
      if (this.#isPanning) {
        // If axis is locked, always use the initial axis, otherwise
        // use the current axis.
        const axis = this.#lockAxis ? this.#initialAxis : this.#panAxis;
        if (!this.#panDetectionAxis.has(axis)) {
          return;
        }

        this.dispatchEvent(
          new CustomEvent(PanEvent.PAN_MOVE, {
            // Default value, but make it clear this is not propagated
            bubbles: false,
            detail: {
              ...panMeasurement,
              axis,
              start: this.#panStart,
              end: this.#panEnd,
            },
          }),
        );
      }
    }

    #handlePanEnd() {
      if (this.#panDetectionAxis.size === 0) {
        return;
      }

      // The user made a touch gesture but less than the threshold
      if (!this.#isPanning) {
        return;
      }

      // Nothing has actually changed here since the last move,
      // since there are no client coordinates for the end event.
      const panMeasurement = this.#measurePan();

      // If axis is locked, always use the initial axis, otherwise
      // use the current axis.
      const axis = this.#lockAxis ? this.#initialAxis : this.#panAxis;
      if (!this.#panDetectionAxis.has(axis)) {
        return;
      }

      this.dispatchEvent(
        new CustomEvent(PanEvent.PAN_END, {
          // Default value, but make it clear this is not propagated
          bubbles: false,
          detail: {
            ...panMeasurement,
            axis,
            start: this.#panStart,
            end: this.#panEnd,
          },
        }),
      );

      this.#resetPan();
    }

    #handlePanCancel() {
      this.#resetPan();

      this.dispatchEvent(
        new CustomEvent(PanEvent.PAN_CANCEL, {
          bubbles: false,
        }),
      );
    }

    /**
     * @param {Coordinates} coordinates Client coordinates
     * @returns {{native: Coordinates, normalized: Coordinates}}
     */
    #measurePanCoordinates(coordinates) {
      // Subtract the element origin in case the element is not at 0,0
      const nativeX = coordinates.x - this.#origin.x;
      const nativeY = coordinates.y - this.#origin.y;
      // Normalize the value (0-1)
      const normalizedX = nativeX / this.#width;
      const normalizedY = nativeY / this.#height;
      return {
        native: { x: nativeX, y: nativeY },
        normalized: { x: normalizedX, y: normalizedY },
      };
    }

    /** @returns {PanMeasurement} */
    #measurePan() {
      if (!(this.#panStart && this.#panEnd)) {
        this.error("Called #measurePan() but #panStart or #panEnd is null.");
      }

      const delta = {
        native: {
          x: this.#panEnd.native.x - this.#panStart.native.x,
          y: this.#panEnd.native.y - this.#panStart.native.y,
        },
        normalized: {
          x: this.#panEnd.normalized.x - this.#panStart.normalized.x,
          y: this.#panEnd.normalized.y - this.#panStart.normalized.y,
        },
      };

      const distance = {
        native: {
          x: Math.abs(delta.native.x),
          y: Math.abs(delta.native.y),
        },
        normalized: {
          x: Math.abs(delta.normalized.x),
          y: Math.abs(delta.normalized.y),
        },
      };

      const axis =
        distance.native.x > distance.native.y ? Axis.HORIZONTAL : Axis.VERTICAL;

      const direction =
        axis === Axis.HORIZONTAL
          ? delta.native.x > 0
            ? Direction.RIGHT
            : Direction.LEFT
          : delta.native.y > 0
            ? Direction.DOWN
            : Direction.UP;

      const duration = this.#panEnd.time - this.#panStart.time;

      const velocity = {
        x: distance.native.x / duration,
        y: distance.native.y / duration,
      };

      return { axis, direction, delta, distance, duration, velocity };
    }

    #resetPan() {
      this.#isPanning = false;
      this.#initialAxis = null;
      this.#panStart = null;
      this.#panEnd = null;
      this.#panAxis = null;
    }

    #addTrackedAxis(state, axis) {
      axis === Axis.ALL
        ? state.add(Axis.HORIZONTAL).add(Axis.VERTICAL)
        : state.add(axis);
    }

    #removeTrackedAxis(state, axis) {
      axis === Axis.ALL ? state.clear() : state.delete(axis);
    }

    #updateCssProperties() {
      let touchAction = "revert";
      if (this.#panDetectionAxis.size === 2) {
        touchAction = "none";
      } else if (this.#panDetectionAxis.has(Axis.HORIZONTAL)) {
        // This is inverted because its what the BROWSER handles
        touchAction = "pan-y";
      } else if (this.#panDetectionAxis.has(Axis.VERTICAL)) {
        // This is inverted because its what the BROWSER handles
        touchAction = "pan-x";
      }
      this.style.setProperty("touch-action", touchAction);

      const userSelect = this.#panDetectionAxis.size > 0 ? "none" : "revert";
      this.style.setProperty("user-select", userSelect);

      const overscrollBehaviorX = this.#panDetectionAxis.has(Axis.HORIZONTAL)
        ? "none"
        : "revert";
      this.style.setProperty("overscroll-behavior-x", overscrollBehaviorX);
      const overscrollBehaviorY = this.#panDetectionAxis.has(Axis.VERTICAL)
        ? "none"
        : "revert";
      this.style.setProperty("overscroll-behavior-y", overscrollBehaviorY);
    }
  };
};

export { PanGesture };
